import React, { useState, useEffect } from 'react'
import {
  Create,
  TextInput,
  useTranslate,
  useNotify,
  TabbedForm,
  FormTab,
  FormDataConsumer,
  useRedirect,
  required,
  BooleanInput,
  NumberInput,
  number,
  minValue,
  TabbedFormTabs,
} from 'react-admin'
import { Grid, Skeleton, TextField, Alert, AlertTitle } from '@mui/material'
import { AdapterToDoRequestComponent } from './logic/AdapterToDoRequestComponent'
import { AdapterToMapperJsonComponent } from './logic/AdapterToMapperJsonComponent'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { isJsonValidate } from 'src/helper/validatorJson'
import { productServices } from 'src/services/reservation/product/product.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'

export const ProductCreate = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [loadingTable, setLoadingTable] = useState(true)
  const [optionsTable, setOptionsTable] = useState([])
  const [json, setJson] = useState('')
  const [productTypes, setProductTypes] = useState([])
  const [loadingProductTypes, setLoadingProductTypes] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const options = await productServices.getDetailsTable()
        setOptionsTable(options.reservation_detail)
        setLoadingTable(false)
      } catch (error) {
        notify(`tables details not found`, { type: 'error' })
        return null
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        setLoadingProductTypes(true)
        const response = await productServices.getAllProductTypes()
        setProductTypes(response)
        setLoadingProductTypes(false)
      } catch (error) {
        notify(`product types not found`, { type: 'error' })
        setLoadingProductTypes(false)
        return null
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeJson = (event) => {
    if (!!event.target.value) {
      setJson(event.target.value)
    }
  }

  const onSuccess = () => {
    notify('ra.notification.created_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.product') },
    })
    redirect('/product')
  }

  const onError = () => {
    notify('ra.notification.created_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.product') },
    })
  }

  return (
    <Create {...props} mutationOptions={{ onSuccess, onError }} actions={null}>
      <CardCommon title={translate('ra.title.create_product')}>
        <TabbedForm
          warnWhenUnsavedChanges
          toolbar={<CardCrudBottomButtons btnSave={true} />}
          tabs={<TabbedFormTabs variant='scrollable' scrollButtons='auto' />}
        >
          <FormTab label={translate('ra.field.main')}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.short_name')} required>
                <TextInput source='short_name' label={false} fullWidth validate={[required()]} />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.long_name')}>
                <TextInput source='long_name' label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.product-type')} required>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='product_type_id'
                    optionsText={['long_name']}
                    optionValue='id'
                    options={productTypes}
                    label=''
                    fullWidth
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.offline_product')}>
                <BooleanInput label={translate('ra.field.offline_product')} source='is_offline' fullWidth />
              </EditInputsWrap>
            </Grid>
          </FormTab>
          <FormTab label={translate('ra.field.mapping')}>
            <Grid container spacing={3} style={{ marginTop: 10 }}>
              <div className='formRelatedFields'>
                <Grid container spacing={3}>
                  <Grid item sm={12}>
                    <Alert severity='info'>
                      <AlertTitle>{translate('ra.title.product_value_assignment')}</AlertTitle>
                      {translate('ra.message.product_value_assignment_text', {
                        json_product: translate('ra.field.json_product'),
                        strongJson: 'Json',
                        detail_table: translate('ra.field.detail_table'),
                        strongTableName: 'tableName',
                      })}
                    </Alert>
                  </Grid>
                  <EditInputsWrap
                    md={6}
                    label={translate('ra.field.product_json')}
                    tooltiptext='Estructura para cargar variables'
                  >
                    <TextField
                      id='json'
                      type='text'
                      label={false}
                      multiline
                      rows='4'
                      onChange={handleChangeJson}
                      fullWidth
                    />
                  </EditInputsWrap>
                  <EditInputsWrap
                    md={6}
                    label={translate('ra.field.ref_table_detail')}
                    tooltiptext='Selección de tabla de....'
                    required
                  >
                    {loadingTable ? (
                      <Skeleton variant='rectangular' width={'100%'} height={40} />
                    ) : (
                      <SelectInput
                        source='ref_table_detail'
                        optionsText={['name']}
                        optionValue='name'
                        options={optionsTable}
                        label=''
                        fullWidth
                        validate={[required()]}
                      />
                    )}
                  </EditInputsWrap>
                  <EditInputsWrap
                    md={6}
                    label={translate('ra.field.json')}
                    tooltiptext='Estructura para cargar variables'
                  >
                    <AdapterToMapperJsonComponent json={json} />
                  </EditInputsWrap>
                  <FormDataConsumer>
                    {({ formData }) => {
                      const { ref_table_detail } = formData
                      return (
                        <EditInputsWrap
                          md={6}
                          label={translate('ra.field.tableName')}
                          tooltiptext='Estructura para cargar variables'
                        >
                          <AdapterToDoRequestComponent tableName={ref_table_detail || ''} />
                        </EditInputsWrap>
                      )
                    }}
                  </FormDataConsumer>
                </Grid>
              </div>
            </Grid>
          </FormTab>
          <FormTab label={translate('ra.field.settings')}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.api_endpoint')} md={3} lg={4}>
                <TextInput source='api_endpoint' label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.tma_code')} md={3} lg={4}>
                <TextInput source='tma_code' label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.days_to_cancel')} md={3} lg={2}>
                <NumberInput
                  source='days_to_cancel'
                  label={false}
                  fullWidth
                  validate={[number(), minValue(0)]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.autocancel')} md={3} lg={2}>
                <BooleanInput source='autocancel' label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.parameters')}>
                <TextInput
                  source='parameters'
                  label={false}
                  multiline
                  rows={5}
                  fullWidth
                  validate={isJsonValidate}
                  format={(v) => {
                    return typeof v === 'object' ? JSON.stringify(v) : v
                  }}
                  parse={(v) => {
                    try {
                      if (v && v.length >= 2) {
                        return JSON.parse(v)
                      }
                      return v
                    } catch (e) {
                      return v || undefined
                    }
                  }}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.status_flow')}>
                <TextInput
                  source='status_flow'
                  label={false}
                  multiline
                  rows={5}
                  fullWidth
                  validate={isJsonValidate}
                  format={(v) => {
                    return typeof v === 'object' ? JSON.stringify(v) : v
                  }}
                  parse={(v) => {
                    try {
                      return v && v.length >= 2 ? JSON.parse(v) : v
                    } catch (e) {
                      return v || undefined
                    }
                  }}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.steps')}>
                <TextInput
                  source='steps'
                  label={false}
                  multiline
                  rows={5}
                  fullWidth
                  validate={isJsonValidate}
                  format={(v) => {
                    return typeof v === 'object' ? JSON.stringify(v) : v
                  }}
                  parse={(v) => {
                    try {
                      return v && v.length >= 2 ? JSON.parse(v) : v
                    } catch (e) {
                      return v || undefined
                    }
                  }}
                />
              </EditInputsWrap>
            </Grid>
          </FormTab>
          <FormTab label={translate('ra.title.notification')}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.folder')}>
                <TextInput source='folder' label={false} fullWidth placeholder='src/emails/...' />
              </EditInputsWrap>
            </Grid>
          </FormTab>
        </TabbedForm>
      </CardCommon>
    </Create>
  )
}
